/* eslint-disable no-underscore-dangle -- fabric's image internals: the fill hook, the live element and the filter scaling */
import type {ImageProps, ImageSource, ObjectEvents, SerializedImageProps, TClassProperties, TOptions} from '@postermywall/fabricjs-2';
import {classRegistry, FabricImage} from '@postermywall/fabricjs-2';
import {degreesToRadians} from '@Utils/math.util';

interface UniqueVideoFrameImageProps {
  /**
   * Degrees the frame turns about the crop window's centre before the window - `cropX`, `cropY`,
   * `width` and `height`, in element pixels - is cut out: a straightened crop, drawn live on every
   * frame. 0 draws fabric's plain rectangle.
   */
  cropRotation: number;
}

export interface VideoFrameImageProps extends ImageProps, UniqueVideoFrameImageProps {}

export const videoFrameImageDefaultValues: Partial<TClassProperties<VideoFrameImage>> = {
  cropRotation: 0,
};

interface FrameSize {
  width: number;
  height: number;
}

/** Fabric's own reading of an element's size: a picture's natural size, anything else's attribute size. */
const getFrameSize = (frame: ImageSource): FrameSize => {
  if (frame instanceof HTMLImageElement) {
    return {width: frame.naturalWidth, height: frame.naturalHeight};
  }
  return {width: frame.width, height: frame.height};
};

/**
 * FabricImage for a playing video. Fabric can only cut an axis-aligned rectangle out of the frame
 * (`cropX`/`cropY`), so a straightened crop draws the whole frame itself, turned about the window's
 * centre and clipped to the window - the drawing `ItemCrop` bakes once for a still picture, repeated
 * here on every frame because the frame keeps changing. Video generation's still-frame path bakes the
 * turn into the frame instead and sets `cropRotation` back to 0.
 */
export class VideoFrameImage<
    Props extends TOptions<VideoFrameImageProps> = Partial<VideoFrameImageProps>,
    SProps extends SerializedImageProps = SerializedImageProps,
    EventSpec extends ObjectEvents = ObjectEvents,
  >
  extends FabricImage<Props, SProps, EventSpec>
  implements UniqueVideoFrameImageProps
{
  declare cropRotation: number;

  static override type = 'VideoFrameImage';

  static override ownDefaults = videoFrameImageDefaultValues;

  static override cacheProperties = [...FabricImage.cacheProperties, 'cropRotation'];

  static override getDefaults(): Record<string, unknown> {
    return {
      ...super.getDefaults(),
      ...VideoFrameImage.ownDefaults,
    };
  }

  constructor(element: ImageSource, options: Props) {
    super(element, options);
    Object.assign(this, VideoFrameImage.ownDefaults);
    this.setOptions(options);
  }

  override _renderFill(ctx: CanvasRenderingContext2D): void {
    if (this.cropRotation === 0) {
      super._renderFill(ctx);
      return;
    }
    const frame = this._element instanceof HTMLVideoElement ? this._applyVideoFilter(this._element) : this._element;
    const frameSize = getFrameSize(frame);
    const halfWidth = this.width / 2;
    const halfHeight = this.height / 2;

    ctx.save();
    ctx.beginPath();
    ctx.rect(-halfWidth, -halfHeight, this.width, this.height);
    ctx.clip();
    ctx.rotate(degreesToRadians(this.cropRotation));
    ctx.drawImage(
      frame,
      0,
      0,
      frameSize.width,
      frameSize.height,
      -(this.cropX + halfWidth),
      -(this.cropY + halfHeight),
      frameSize.width / this._filterScalingX,
      frameSize.height / this._filterScalingY
    );
    ctx.restore();
  }
}

classRegistry.setClass(VideoFrameImage);
